/* ============================================
   LOADING SCREEN COMPONENT
   The Creative Orchestra - GEMINI Framework
   Initial loading screen with animations
   ============================================ */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';

/**
 * Loading Screen Component
 * Full-screen loading animation shown on initial page load
 * 
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether to show loading screen
 * @param {number} props.minDuration - Minimum display duration (ms)
 * @param {Function} props.onComplete - Callback when loading completes
 */
const LoadingScreen = ({ 
  isLoading = true, 
  minDuration = 1500,
  onComplete,
}) => {
  const [showLoader, setShowLoader] = useState(true);
  const [progress, setProgress] = useState(0);

  // Simulate loading progress
  useEffect(() => {
    if (!isLoading) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / minDuration) * 100, 100);
      setProgress(newProgress);

      if (elapsed >= minDuration) {
        clearInterval(interval);
        setTimeout(() => {
          setShowLoader(false);
          onComplete?.();
        }, 300);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [isLoading, minDuration, onComplete]);

  return (
    <AnimatePresence>
      {showLoader && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center
            bg-[#0a0a0f]"
        >
          {/* Background stars */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(50)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  opacity: [0.2, 0.8, 0.2],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>

          {/* Main content */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Logo orb */}
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                boxShadow: [
                  '0 0 30px rgba(129, 140, 248, 0.3)',
                  '0 0 60px rgba(129, 140, 248, 0.5)',
                  '0 0 30px rgba(129, 140, 248, 0.3)',
                ],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="w-20 h-20 rounded-full mb-8
                bg-gradient-to-br from-indigo-500/30 to-purple-500/30
                border border-white/20 flex items-center justify-center"
            >
              <Sparkles className="w-8 h-8 text-white" />
            </motion.div>

            {/* GEMINI text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center mb-8"
            >
              <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-2">
                GEMINI
              </h1>
              <p className="text-sm text-gray-400">
                The Creative Orchestra
              </p>
            </motion.div>

            {/* Progress bar */}
            <div className="w-48 h-1 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>

            {/* Loading text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-4 text-xs text-gray-500"
            >
              Loading experience...
            </motion.p>
          </div>

          {/* Decorative rings */}
          <motion.div
            className="absolute w-64 h-64 rounded-full border border-white/5"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute w-96 h-96 rounded-full border border-white/5"
            animate={{ rotate: -360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Simple Loader Component
 * Smaller inline loader for content sections
 */
export const InlineLoader = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <motion.div
        className={`${sizes[size]} border-2 border-white/20 border-t-white/80 rounded-full`}
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
};

/**
 * Skeleton Loader Component
 * Placeholder for loading content
 */
export const Skeleton = ({ 
  className = '', 
  variant = 'text',
  lines = 1,
}) => {
  const variants = {
    text: 'h-4 rounded',
    title: 'h-8 rounded w-3/4',
    paragraph: 'h-4 rounded',
    circle: 'rounded-full',
    card: 'h-48 rounded-2xl',
    image: 'aspect-video rounded-xl',
  };

  if (variant === 'paragraph' && lines > 1) {
    return (
      <div className={`space-y-2 ${className}`}>
        {[...Array(lines)].map((_, i) => (
          <motion.div
            key={i}
            className={`bg-white/10 ${variants.paragraph}`}
            style={{ width: i === lines - 1 ? '60%' : '100%' }}
            animate={{ opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.1 }}
          />
        ))}
      </div>
    );
  }

  return (
    <motion.div
      className={`bg-white/10 ${variants[variant]} ${className}`}
      animate={{ opacity: [0.5, 0.8, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity }}
    />
  );
};

/**
 * Page Loader Component
 * Centered loader for page content
 */
export const PageLoader = ({ message = 'Loading...' }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh]">
      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.5, 1, 0.5],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/30
          border border-white/20 flex items-center justify-center mb-4"
      >
        <Sparkles className="w-6 h-6 text-white" />
      </motion.div>
      <p className="text-sm text-gray-400">{message}</p>
    </div>
  );
};

export default LoadingScreen;
